import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  GetEventPageByIdSlice,
  UpdateEventPageSlice,
  AddEventPageReviewSlice,
  UpdateEventPageReviewSlice,
  DeleteEventPageReviewSlice,
  clearSelectedEventPage,
} from "../../../redux/features/EventPageManagementSlice";
import Editor from "../../../common/Editor/Editor";
import { ReviewCardList } from "../ReviewSection";

const EditEventPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedEventPage, loading, reviewLoading } = useSelector(
    (state) => state.EventPageManagement
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    h1Title: "",
    pageDescription: "",
    content: "",
    status: "active",
  });
  // prefill only once per page, so saving a review doesn't reset unsaved form edits
  const prefilledId = useRef(null);

  /* Fetch by id */
  useEffect(() => {
    if (id) {
      prefilledId.current = null;
      dispatch(clearSelectedEventPage());
      dispatch(GetEventPageByIdSlice(id));
    }
  }, [id, dispatch]);

  /* Prefill form */
  useEffect(() => {
    if (selectedEventPage && selectedEventPage._id === id && prefilledId.current !== id) {
      prefilledId.current = id;
      setForm({
        title: selectedEventPage.title || "",
        slug: selectedEventPage.slug || "",
        description: selectedEventPage.description || "",
        h1Title: selectedEventPage.h1Title || "",
        pageDescription: selectedEventPage.pageDescription || "",
        content: selectedEventPage.content || "",
        status: selectedEventPage.status || "active",
      });
    }
  }, [selectedEventPage, id]);

  /* Cleanup on unmount */
  useEffect(() => {
    return () => {
      dispatch(clearSelectedEventPage());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!id) return;

    setIsSubmitting(true);

    await dispatch(
      UpdateEventPageSlice({
        id,
        payload: form,
      })
    );

    setIsSubmitting(false);
    navigate("/EventPageManagement");
  };

  /* Reviews - each action is saved immediately through the review APIs */
  const handleAddReview = (payload) => dispatch(AddEventPageReviewSlice({ id, payload }));
  const handleUpdateReview = (reviewId, payload) =>
    dispatch(UpdateEventPageReviewSlice({ id, reviewId, payload }));
  const handleDeleteReview = (reviewId) => dispatch(DeleteEventPageReviewSlice({ id, reviewId }));

  if (loading || !selectedEventPage) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div className="Small-Wrapper mt-4">
      {/* Title */}
      <div className="form-group">
        <label>Title</label>
        <input
          className="form-control"
          name="title"
          value={form.title}
          onChange={handleChange}
        />
      </div>

      {/* Slug */}
      <div className="form-group">
        <label>Slug</label>
        <input
          className="form-control"
          name="slug"
          value={form.slug}
          onChange={handleChange}
        />
      </div>

      {/* Description */}
      <div className="form-group">
        <label>Description</label>
        <textarea
          className="form-control"
          name="description"
          value={form.description}
          onChange={handleChange}
        />
      </div>

      {/* H1 Title */}
      <div className="form-group">
        <label>H1 Title</label>
        <input
          className="form-control"
          name="h1Title"
          value={form.h1Title}
          onChange={handleChange}
        />
      </div>

      {/* Page Description */}
      <div className="form-group">
        <label>Page Description</label>
        <textarea
          className="form-control"
          name="pageDescription"
          value={form.pageDescription}
          onChange={handleChange}
        />
      </div>

      {/* Status */}
      <div className="form-group">
        <label>Status</label>
        <select
          name="status"
          className="form-control"
          value={form.status}
          onChange={handleChange}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Content */}
      <div className="form-group">
        <label>Content</label>
        <Editor
          setContentState={setForm}
          type="content"
          content={form.content}
          form={form}
        />
      </div>

      {/* Reviews */}
      <ReviewCardList
        reviews={selectedEventPage.reviews || []}
        loading={reviewLoading}
        onAdd={handleAddReview}
        onUpdate={handleUpdateReview}
        onDelete={handleDeleteReview}
      />

      <button
        className="Button mt-3"
        disabled={isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? "Updating..." : "Update"}
      </button>
    </div>
  );
};

export default EditEventPage;
